import React, { useEffect, useState } from "react";
import { Loader2, Download, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, API } from "@/lib/api";
import { useTranslation } from "react-i18next";

const IMG = ["jpg", "jpeg", "png", "webp", "heic", "gif"];
const VID = ["mp4", "webm", "mov", "m4v"];

export default function DocPreviewModal({ doc, onClose, prefix = "/admin/files" }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const open = !!doc;
  const ext = (doc?.filename?.split(".").pop() || "").toLowerCase();
  const isImg = IMG.includes(ext);
  const isPdf = ext === "pdf";
  const isVid = VID.includes(ext);

  useEffect(() => {
    if (!doc) return;
    let objectUrl;
    setLoading(true); setError(false); setUrl(null);
    (async () => {
      try {
        const res = await api.get(`${prefix}/${doc.path}`, { responseType: "blob" });
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      } catch { setError(true); } finally { setLoading(false); }
    })();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [doc]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl bg-card" data-testid="doc-preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground pr-8">
            <FileText className="h-4 w-4 text-orange-600 shrink-0" />
            <span className="truncate">{doc?.filename || t("client.document")}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="min-h-[300px] max-h-[70vh] overflow-auto rounded-md bg-secondary/40 grid place-items-center">
          {loading && <Loader2 className="h-6 w-6 animate-spin text-orange-600" data-testid="doc-preview-loading" />}
          {!loading && error && <p className="text-sm text-muted-foreground py-10" data-testid="doc-preview-error">{t("client.previewError")}</p>}
          {!loading && url && isImg && <img src={url} alt={doc.filename} className="max-w-full h-auto" data-testid="doc-preview-image" />}
          {!loading && url && isPdf && <iframe src={url} title={doc.filename} className="w-full h-[65vh] border-0" data-testid="doc-preview-pdf" />}
          {!loading && url && isVid && <video src={url} controls className="max-w-full max-h-[65vh]" data-testid="doc-preview-video" />}
          {!loading && url && !isImg && !isPdf && !isVid && (
            <div className="py-10 text-center">
              <p className="text-sm text-muted-foreground">{t("client.previewUnavailable")}</p>
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <a href={`${API}${prefix}/${doc?.path || ""}`} target="_blank" rel="noreferrer" download>
            <Button variant="outline" size="sm" className="rounded-sm border-slate-300 bg-card text-xs" data-testid="doc-preview-download">
              <Download className="h-3.5 w-3.5 mr-1" /> {t("client.download")}
            </Button>
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
